/**
 * Shared editor state for the anchored-gutter comments UX (#2244).
 *
 * The old `CommentThreadModal` and its `modal` state are gone — the
 * ticket's central AC was "no modal in the comment path". Compose
 * flows through a single live draft on the context; the gutter mounts
 * an inline-edit `CommentCard` at the draft's anchor and the user
 * saves or Escapes inside it. View, edit and delete are all handled
 * inside each `CommentCard` (`view` / `edit` / `confirm-delete`
 * modes). Resolve/unresolve (#2246) still happens from the view-mode
 * actions bar.
 *
 * Paragraph-leaf markers (amber/emerald underlines + icons) now serve
 * as discoverability hints only — clicking a marker scrolls the
 * matching gutter card into view instead of opening a modal.
 */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

import type { Comment } from '@utils/types/comments/comments.ts';
import type { DocumentTranscriptUtteranceV1 } from '@utils/types/editors/transcripts/transcript.ts';
import { useGetComments } from '@utils/api/comments/use-get-comments.tsx';
import { useCommentFilter } from './comments-filter-context.tsx';
import {
  commentsEndingAtStop,
  commentsOverlappingWord,
  filterFlagsFor,
  textForRange
} from './comments-selectors.ts';
import { useCommentMutations } from './use-comment-mutations.ts';

export interface CommentDraft {
  /** `index_from` of the selected word range (ms, string). */
  indexFrom: string;
  /** `index_to` of the selected word range (ms, string). */
  indexTo: string;
}

interface CommentsContextValue {
  sessionId: string;
  documentId: string;
  loading: boolean;
  allComments: Comment[];
  openComments: Comment[];
  /** Open comments covering this word — drives the amber marker. */
  commentsAtWord: (
    leafStart: number | undefined,
    leafStop: number | undefined
  ) => Comment[];
  /** Open comments ending at this word — drives the amber icon. */
  commentsEndingAt: (leafStop: number | undefined) => Comment[];
  /** Resolved comments covering this word — drives the green marker. */
  resolvedCommentsAtWord: (
    leafStart: number | undefined,
    leafStop: number | undefined
  ) => Comment[];
  /** Resolved comments ending at this word — drives the green icon. */
  resolvedCommentsEndingAt: (leafStop: number | undefined) => Comment[];
  /** The in-flight draft comment, or `null` when no compose is open. */
  draft: CommentDraft | null;
  /** Open a fresh compose draft at the selected word range. */
  startDraft: (range: CommentDraft) => void;
  /** Discard the current draft without saving. */
  cancelDraft: () => void;
  createOne: (payload: {
    text: string;
    indexFrom: string;
    indexTo: string;
  }) => Promise<void>;
  updateOne: (
    id: number,
    text: string,
    indexFrom: string,
    indexTo: string
  ) => Promise<void>;
  deleteOne: (id: number) => Promise<void>;
  toggleResolved: (comment: Comment) => Promise<void>;
  readOnly: boolean;
  /**
   * Visual palette for inline leaf adornments. 'default' = amber/emerald
   * (the author's own comments on their own pane). 'muted' = slate for
   * the opposite-pane comments shown read-only in split view (#2246);
   * the AC asks source and translation comments to be distinguishable
   * at a glance without hovering.
   */
  tone: 'default' | 'muted';
  ownershipLabel?: string;
  getTextForRange: (
    indexFrom: string | undefined,
    indexTo: string | undefined
  ) => string;
}

const CommentsContext = createContext<CommentsContextValue | null>(null);

export function useCommentsContext(): CommentsContextValue | null {
  return useContext(CommentsContext);
}

export interface CommentsProviderProps {
  sessionId: string;
  documentId: string;
  /** Hide edit/delete/resolve/compose affordances. Used for source
   *  pane in split view (#2246). */
  readOnly?: boolean;
  /**
   * Visual palette override — 'muted' switches inline-leaf adornments
   * from amber/emerald to slate so a reader of the translation pane
   * can tell at a glance that a comment lives on the source. Defaults
   * to 'default'. See `useLeafCommentAdornments` for the colour table.
   */
  tone?: 'default' | 'muted';
  /** Optional pill label rendered on each thread ("Translation"). */
  ownershipLabel?: string;
  /** Utterances from the pane's own transcript context — used to
   *  render the anchored text preview above each comment card. */
  documentUtterances?: DocumentTranscriptUtteranceV1[];
  /**
   * #2250 — override the anchored-text lookup. When passed, the modal
   * reads the text preview from this function instead of walking the
   * transcript's utterance shape. Caption editors supply a segment
   * lookup: `(from, to) => segments.find(s => s.start.toString() === from
   * && s.stop.toString() === to)?.text ?? ''`. Cheaper than teaching
   * `getTextForRange` two shapes and matches the "no isCaption prop"
   * spirit — every caller declares how its own document names ranges.
   */
  getAnchoredText?: (
    indexFrom: string | undefined,
    indexTo: string | undefined
  ) => string;
  children: ReactNode;
}

export function CommentsProvider({
  sessionId,
  documentId,
  readOnly = false,
  tone = 'default',
  ownershipLabel,
  documentUtterances,
  getAnchoredText,
  children
}: CommentsProviderProps) {
  const [draft, setDraft] = useState<CommentDraft | null>(null);
  const { filter } = useCommentFilter();
  const { showOpen, showResolved } = filterFlagsFor(filter);

  const { data, isLoading } = useGetComments({
    session_id: sessionId,
    document_id: documentId
  });
  const mutations = useCommentMutations(sessionId, documentId);

  const allComments = useMemo(() => data?.result ?? [], [data]);
  const openComments = useMemo(
    () => allComments.filter(c => !c.resolved),
    [allComments]
  );
  const resolvedComments = useMemo(
    () => allComments.filter(c => !!c.resolved),
    [allComments]
  );

  const commentsAtWord = useCallback(
    (leafStart: number | undefined, leafStop: number | undefined) =>
      showOpen
        ? commentsOverlappingWord(openComments, leafStart, leafStop)
        : [],
    [openComments, showOpen]
  );
  const commentsEndingAt = useCallback(
    (leafStop: number | undefined) =>
      showOpen ? commentsEndingAtStop(openComments, leafStop) : [],
    [openComments, showOpen]
  );
  const resolvedCommentsAtWord = useCallback(
    (leafStart: number | undefined, leafStop: number | undefined) =>
      showResolved
        ? commentsOverlappingWord(resolvedComments, leafStart, leafStop)
        : [],
    [resolvedComments, showResolved]
  );
  const resolvedCommentsEndingAt = useCallback(
    (leafStop: number | undefined) =>
      showResolved ? commentsEndingAtStop(resolvedComments, leafStop) : [],
    [resolvedComments, showResolved]
  );

  const getTextForRange = useCallback(
    (indexFrom: string | undefined, indexTo: string | undefined) => {
      // #2250 — caller-supplied lookup takes precedence. The caption
      // editor passes one that maps [from, to] to the segment's text;
      // the transcript editor falls through to `textForRange`, which
      // walks the utterance shape. Keeps the "no isCaption prop" rule
      // — each caller declares how its own document names ranges.
      if (getAnchoredText) return getAnchoredText(indexFrom, indexTo);
      return textForRange(documentUtterances, indexFrom, indexTo);
    },
    [documentUtterances, getAnchoredText]
  );

  const startDraft = useCallback(
    (range: CommentDraft) => {
      if (readOnly) return;
      setDraft(range);
    },
    [readOnly]
  );
  const cancelDraft = useCallback(() => setDraft(null), []);

  const value = useMemo<CommentsContextValue>(
    () => ({
      sessionId,
      documentId,
      loading: isLoading,
      allComments,
      openComments,
      commentsAtWord,
      commentsEndingAt,
      resolvedCommentsAtWord,
      resolvedCommentsEndingAt,
      draft,
      startDraft,
      cancelDraft,
      ...mutations,
      readOnly,
      tone,
      ownershipLabel,
      getTextForRange
    }),
    [
      sessionId,
      documentId,
      isLoading,
      allComments,
      openComments,
      commentsAtWord,
      commentsEndingAt,
      resolvedCommentsAtWord,
      resolvedCommentsEndingAt,
      draft,
      startDraft,
      cancelDraft,
      mutations,
      readOnly,
      tone,
      ownershipLabel,
      getTextForRange
    ]
  );

  return (
    <CommentsContext.Provider value={value}>
      {children}
    </CommentsContext.Provider>
  );
}
